import React from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { Kicker as BaseKicker, SectionEyebrow, SectionTitle } from "./Components/common/Section";
import imageUrl_OC from "../images/Original_Compositions_RMS.png";
import imageUrl_DI from "../images/Distribution.png";
import imageUrl_CL from "../images/Transaction_Illustration.png";
import imageUrl_DV from "../images/Genre_Diversity.png";
import Rms_logo from "../images/RMS_logo.png";

const Page = styled.section`
  overflow: clip;
`;

const IntroSection = styled.div`
  position: relative;
  padding: clamp(7rem, 12vw, 10rem) 8vw 5rem;

  @media (max-width: 480px) {
    padding-left: 5vw;
    padding-right: 5vw;
  }
  background:
    radial-gradient(circle at 80% 20%, rgba(214, 179, 106, 0.12), transparent 26%),
    linear-gradient(180deg, rgba(11, 11, 13, 0.92), rgba(5, 5, 6, 1));

  &::after {
    content: "";
    position: absolute;
    inset: auto 8vw 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--ink-12), transparent);
  }
`;

const IntroGrid = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: clamp(2rem, 5vw, 4rem);
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
`;

const LogoWrap = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--ink-12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }
`;

const Kicker = styled(BaseKicker)`
  @media (max-width: 768px) { justify-content: center; }
`;

const Headline = styled.h1`
  font-family: "DM Serif Display", serif;
  font-size: clamp(2.8rem, 6vw, 5rem);
  line-height: 0.95;
  letter-spacing: -0.03em;
  margin-bottom: 20px;
`;

const Lead = styled.p`
  max-width: 52rem;
  color: var(--ink-78);
  line-height: 1.85;
  font-size: clamp(1rem, 1.4vw, 1.12rem);
`;

const HighlightsSection = styled.section`
  padding: clamp(4rem, 6vw, 5.5rem) 8vw;

  @media (max-width: 480px) {
    padding-left: 5vw;
    padding-right: 5vw;
  }
`;

const HighlightList = styled.div`
  display: grid;
  border-top: 1px solid var(--ink-10);
`;

const HighlightRow = styled.div`
  display: grid;
  grid-template-columns: 90px minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 20px;
  padding: 24px 0;
  border-bottom: 1px solid var(--ink-08);
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const HighlightIndex = styled.span`
  font-size: 12px;
  color: var(--accent-2);
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const HighlightTitle = styled.h3`
  font-size: 1.25rem;
  color: var(--ink);
`;

const HighlightText = styled.p`
  color: var(--muted);
  line-height: 1.8;
`;

const WhySection = styled.section`
  padding: clamp(4rem,6vw,5.5rem) 8vw;
  border-top: 1px solid var(--ink-06);
  @media (max-width: 480px) { padding-left: 5vw; padding-right: 5vw; }
`;

const WhyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px,1fr));
  gap: 24px;
  margin-top: 28px;
`;

const WhyCard = styled.div`
  padding: 28px 24px;
  border: 1px solid rgba(243,239,230,.08);
  background: rgba(255,255,255,.02);
  h3 { font-family: "DM Serif Display", serif; font-size: 1.3rem; color: var(--ink); margin-bottom: 12px; }
  p { color: var(--muted); line-height: 1.8; font-size: 14px; }
`;

const SectionsArea = styled.section`
  padding: 0 8vw clamp(4rem, 6vw, 5.5rem);

  @media (max-width: 480px) {
    padding-left: 5vw;
    padding-right: 5vw;
  }
`;

const ContentBlock = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(280px, 0.9fr);
  gap: clamp(2rem, 5vw, 4rem);
  align-items: center;
  padding: clamp(3rem, 5vw, 4.5rem) 0;
  border-bottom: 1px solid var(--ink-06);

  &:nth-child(even) {
    direction: rtl;
    > * {
      direction: ltr;
    }
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    &:nth-child(even) {
      direction: ltr;
    }
  }
`;

const BlockText = styled.div`
  h2 {
    font-family: "DM Serif Display", serif;
    font-size: clamp(1.8rem, 3vw, 2.6rem);
    line-height: 1.05;
    margin-bottom: 16px;
  }

  p {
    max-width: 48ch;
    color: var(--ink-78);
    line-height: 1.85;
  }

  span {
    color: var(--accent);
    font-weight: 600;
  }
`;

const BlockImage = styled.div`
  border: 1px solid var(--ink-08);
  background:
    radial-gradient(circle at top, rgba(214, 179, 106, 0.08), transparent 40%),
    rgba(255, 255, 255, 0.04);
  padding: 18px;
  display: grid;
  place-items: center;
  min-height: 280px;

  img {
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: contain;
    object-position: center;
  }
`;

const About = () => {
  return (
    <Page>
      <Helmet>
        <title>About RMS — Regime Music Societe | Global Artist Services</title>
        <meta
          name="description"
          content="Regime Music Societe — Atlanta-based publishing company. Artist-first strategy, global partnerships, and culture-led projects for iconic releases."
        />
        <meta property="og:title" content="About Regime Music Societe" />
        <meta
          property="og:description"
          content="Global artist services and music publishing company founded in Atlanta."
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://rmsociete.com/images/REGIME_logo.svg" />
        <meta property="og:url" content="https://rmsociete.com/about" />
        <meta property="og:image" content="https://rmsociete.com/images/REGIME_logo.svg" />
        <link rel="canonical" href="https://rmsociete.com/about" />
        <meta
          name="keywords"
          content="Regime Music, publishing, artist services, Atlanta, music production, collaborations"
        />
      </Helmet>

      <IntroSection>
        <IntroGrid>
          <LogoWrap>
            <img loading="lazy" src={Rms_logo} alt="RMS logo" />
          </LogoWrap>
          <div>
            <Kicker>About the company</Kicker>
            <Headline>Regime Music Societe</Headline>
            <Lead>
              RMS is a global artist services and music publishing company founded in
              Atlanta, Georgia. We create and manage original compositions, partner
              with international labels, and deliver cultural moments that move
              audiences worldwide.
            </Lead>
          </div>
        </IntroGrid>
      </IntroSection>

      <HighlightsSection>
        <SectionEyebrow>What defines RMS</SectionEyebrow>
        <SectionTitle>Built on taste, rights clarity, and global reach.</SectionTitle>
        <HighlightList>
          <HighlightRow>
            <HighlightIndex>01</HighlightIndex>
            <HighlightTitle>Artist-first strategy</HighlightTitle>
            <HighlightText>
              We build long-term careers through curated catalogs, publishing
              intelligence, and creative collaboration — not just release volume.
            </HighlightText>
          </HighlightRow>
          <HighlightRow>
            <HighlightIndex>02</HighlightIndex>
            <HighlightTitle>Global partnerships</HighlightTitle>
            <HighlightText>
              RMS works with major labels, distributors, and streaming platforms to
              unlock new markets and amplify cross-border reach.
            </HighlightText>
          </HighlightRow>
          <HighlightRow>
            <HighlightIndex>03</HighlightIndex>
            <HighlightTitle>Culture-led projects</HighlightTitle>
            <HighlightText>
              From FIFA collaborations to branded sonic identities, we deliver
              standout moments for global audiences with cultural credibility.
            </HighlightText>
          </HighlightRow>
        </HighlightList>
      </HighlightsSection>

      <WhySection>
        <SectionEyebrow>Why RMS</SectionEyebrow>
        <SectionTitle>Why clients work with us.</SectionTitle>
        <WhyGrid>
          <WhyCard>
            <h3>Rights-first approach</h3>
            <p>Every project starts with publishing clarity — splits, registrations, and administration handled before release, not after.</p>
          </WhyCard>
          <WhyCard>
            <h3>Culture fluency</h3>
            <p>We understand what lands across markets. From Afrobeats to Latin crossover, RMS operates where genre boundaries blur and audiences grow.</p>
          </WhyCard>
          <WhyCard>
            <h3>Execution speed</h3>
            <p>Release architecture built for momentum, not bureaucracy. We move fast without cutting corners on rights or creative quality.</p>
          </WhyCard>
        </WhyGrid>
      </WhySection>

      <SectionsArea>
        <ContentBlock>
          <BlockText>
            <h2>Original compositions</h2>
            <p>
              RMS conceptualizes, creates, and manages revenue for in-house
              compositions distributed across leading publishers. We have served as
              a consultant for <span>FIFA Sound</span> to curate original works for
              official tournament anthems.
            </p>
          </BlockText>
          <BlockImage>
            <img loading="lazy" src={imageUrl_OC} alt="Original composition" />
          </BlockImage>
        </ContentBlock>

        <ContentBlock>
          <BlockText>
            <h2>Genre diversity</h2>
            <p>
              Our catalog spans Afro Beats, Reggaeton, American Pop, and more.
              RMS compositions are supported by <span>major label</span> and
              publishing agreements that amplify global reach.
            </p>
          </BlockText>
          <BlockImage>
            <img loading="lazy" src={imageUrl_DV} alt="Genre diversity" />
          </BlockImage>
        </ContentBlock>

        <ContentBlock>
          <BlockText>
            <h2>Collaborations</h2>
            <p>
              We collaborate with artists, musicians, and composers worldwide to
              create iconic anthems that bridge cultures and build communities.
            </p>
          </BlockText>
          <BlockImage>
            <img loading="lazy" src={imageUrl_CL} alt="Collaboration" />
          </BlockImage>
        </ContentBlock>

        <ContentBlock>
          <BlockText>
            <h2>Distribution</h2>
            <p>
              Regime partners with top distributors and publishers to deliver
              releases that meet the highest standards of quality and reach.
            </p>
          </BlockText>
          <BlockImage>
            <img loading="lazy" src={imageUrl_DI} alt="Distribution" />
          </BlockImage>
        </ContentBlock>
      </SectionsArea>
    </Page>
  );
};

export default About;
